/**
 * AudioMetadataStripper
 *
 * Byte-level ID3 tag and iTunes-style metadata stripping for audio files.
 * Does NOT rely on browser audio decoders — all parsing is deterministic
 * byte arithmetic on the raw Uint8Array.
 *
 * Supported formats:
 *   - audio/mpeg  (MP3): strips ID3v2 at start, ID3v1 tag at end (128 bytes),
 *                        and ID3v2 appended after the audio data.
 *   - audio/mp4   (M4A/AAC in MPEG-4 container): strips iTunes-style `udta`
 *                        metadata box from the MPEG-4 container while
 *                        preserving the `moov`, `mdat`, `ftyp`, and `moof`
 *                        boxes needed for playback.
 *   - audio/ogg   (Ogg Vorbis/Opus): strips Vorbis COMMENT packets from the
 *                        Ogg page stream while preserving the audio pages.
 *
 * Design note:
 *   Signal-iOS uses AVFoundation metadata APIs; on web there is no equivalent
 *   platform API. We implement deterministic byte-level parsers that match the
 *   published format specifications so stripping is reproducible and testable
 *   without any browser media stack involvement.
 */

export type SupportedAudioMime = "audio/mpeg" | "audio/mp4" | "audio/ogg";

// ---------------------------------------------------------------------------
// stripAudioMetadata — main export
// ---------------------------------------------------------------------------

/**
 * Strips metadata from `bytes` according to `mime`.
 *
 * Returns the sanitised bytes. If `bytes` carries no recognised metadata the
 * return value is a copy of the input (never a reference to the same buffer,
 * so callers can safely dispose of `bytes`).
 *
 * Throws if the container structure is corrupt beyond what the parser can
 * tolerate (e.g. a declared box/tag length exceeds the buffer). The caller
 * (`UploadSanitiser`) maps this to `{ ok: false, reason: "stripFailed" }`.
 */
export function stripAudioMetadata(
  bytes: Uint8Array,
  mime: SupportedAudioMime,
): Uint8Array {
  switch (mime) {
    case "audio/mpeg":
      return _stripMp3(bytes);
    case "audio/mp4":
      return _stripMp4Audio(bytes);
    case "audio/ogg":
      return _stripOgg(bytes);
  }
}

// ---------------------------------------------------------------------------
// MP3 / ID3 stripping
// ---------------------------------------------------------------------------

/**
 * ID3v2 header layout (10 bytes):
 *   "ID3" (3)  version (2)  flags (1)  size (4 × 7-bit syncsafe)
 *
 * The syncsafe size encodes the tag body length EXCLUDING the 10-byte header.
 * After stripping the tag we may still find another ID3v2 (appended tags) or
 * an ID3v1 trailer (last 128 bytes starting with "TAG").
 */
function _stripMp3(bytes: Uint8Array): Uint8Array {
  let start = 0;

  // Strip all leading ID3v2 tags (multiple are valid per spec)
  while (start + 10 <= bytes.length) {
    if (bytes[start] === 0x49 && bytes[start + 1] === 0x44 && bytes[start + 2] === 0x33) {
      // "ID3"
      const size = _id3v2SyncsafeSize(bytes, start + 6);
      const tagTotal = 10 + size;
      if (start + tagTotal > bytes.length) {
        throw new Error(`ID3v2: declared tag length ${tagTotal} exceeds buffer`);
      }
      start += tagTotal;
    } else {
      break;
    }
  }

  // Determine end: strip ID3v1 trailer if present
  let end = bytes.length;
  if (end - start >= 128) {
    const tailOffset = end - 128;
    if (
      bytes[tailOffset] === 0x54 &&
      bytes[tailOffset + 1] === 0x41 &&
      bytes[tailOffset + 2] === 0x47
    ) {
      // "TAG"
      end = tailOffset;
    }
  }

  // Within the audio data there may also be appended ID3v2 tags (rare but
  // legal). Scan for them and stop at the last clean byte.
  // We do a single forward pass to find any interior ID3v2 markers.
  let clean = start;
  let pos = start;
  while (pos + 10 <= end) {
    if (bytes[pos] === 0x49 && bytes[pos + 1] === 0x44 && bytes[pos + 2] === 0x33) {
      const size = _id3v2SyncsafeSize(bytes, pos + 6);
      const tagTotal = 10 + size;
      if (pos + tagTotal > end) break; // malformed, stop scanning
      // We found an appended tag: truncate output here
      end = pos;
      break;
    }
    // Skip MPEG sync frames roughly (0xFF then 0xE0-0xFF)
    pos++;
  }
  void clean;

  if (start === 0 && end === bytes.length) {
    return bytes.slice(); // no tags found — return a copy
  }
  return bytes.slice(start, end);
}

/**
 * Decode a 4-byte syncsafe integer at `offset`. Each byte contributes 7 bits.
 */
function _id3v2SyncsafeSize(bytes: Uint8Array, offset: number): number {
  return (
    ((bytes[offset]! & 0x7f) << 21) |
    ((bytes[offset + 1]! & 0x7f) << 14) |
    ((bytes[offset + 2]! & 0x7f) << 7) |
    (bytes[offset + 3]! & 0x7f)
  );
}

// ---------------------------------------------------------------------------
// MPEG-4 audio (M4A) — strip `udta` box
// ---------------------------------------------------------------------------

/**
 * MPEG-4 / ISO Base Media File Format box layout:
 *   size (4 big-endian uint32)  type (4 ASCII)  data (size-8 bytes)
 *   Special: size=0 means "to end of file"; size=1 means 64-bit large size
 *   follows (8 bytes after type).
 *
 * We rebuild the file by collecting all top-level boxes EXCEPT:
 *   - `udta` (iTunes tags, Nero tags, etc.)
 *   - `meta` when it is a sibling of `moov` (not the QuickTime metadata)
 *   - `free` and `skip` (padding boxes — safe to drop)
 *
 * The `moov` box itself may contain a nested `udta`; we recurse into `moov`
 * to strip it there too.
 */
function _stripMp4Audio(bytes: Uint8Array): Uint8Array {
  const chunks: Uint8Array[] = [];
  _mp4StripBoxes(bytes, 0, bytes.length, chunks, /* topLevel */ true);
  return _concat(chunks);
}

function _mp4StripBoxes(
  bytes: Uint8Array,
  start: number,
  end: number,
  out: Uint8Array[],
  topLevel: boolean,
): void {
  let pos = start;
  while (pos + 8 <= end) {
    const size32 =
      ((bytes[pos]! << 24) |
        (bytes[pos + 1]! << 16) |
        (bytes[pos + 2]! << 8) |
        bytes[pos + 3]!) >>>
      0;
    const type = String.fromCharCode(bytes[pos + 4]!, bytes[pos + 5]!, bytes[pos + 6]!, bytes[pos + 7]!);

    let headerLen: number;
    let boxLen: number;

    if (size32 === 1) {
      // 64-bit large size
      if (pos + 16 > end) throw new Error("MP4: large size box truncated");
      // We only support containers ≤ 4GB for now; read upper 32 bits and
      // assert they are zero for 25MB file constraint.
      const hi =
        ((bytes[pos + 8]! << 24) |
          (bytes[pos + 9]! << 16) |
          (bytes[pos + 10]! << 8) |
          bytes[pos + 11]!) >>>
        0;
      if (hi !== 0) throw new Error("MP4: box > 4GB not supported");
      const lo =
        ((bytes[pos + 12]! << 24) |
          (bytes[pos + 13]! << 16) |
          (bytes[pos + 14]! << 8) |
          bytes[pos + 15]!) >>>
        0;
      boxLen = lo;
      headerLen = 16;
    } else if (size32 === 0) {
      // "rest of file" box
      boxLen = end - pos;
      headerLen = 8;
    } else {
      boxLen = size32;
      headerLen = 8;
    }

    if (pos + boxLen > end || boxLen < headerLen) {
      throw new Error(`MP4: box "${type}" declared size ${boxLen} exceeds buffer`);
    }

    const boxEnd = pos + boxLen;

    // Drop metadata boxes
    if (type === "udta" || type === "free" || type === "skip") {
      pos = boxEnd;
      continue;
    }

    // Strip nested udta inside moov
    if (type === "moov" || type === "trak" || type === "mdia" || type === "minf" || type === "stbl") {
      const innerOut: Uint8Array[] = [];
      _mp4StripBoxes(bytes, pos + headerLen, boxEnd, innerOut, false);
      // Rebuild box with stripped inner content
      const innerBytes = _concat(innerOut);
      const newBoxLen = headerLen + innerBytes.length;
      const header = new Uint8Array(headerLen);
      // Write new size (32-bit; inner content is always < 4GB due to 25MB cap)
      header[0] = (newBoxLen >>> 24) & 0xff;
      header[1] = (newBoxLen >>> 16) & 0xff;
      header[2] = (newBoxLen >>> 8) & 0xff;
      header[3] = newBoxLen & 0xff;
      // Copy type
      header[4] = bytes[pos + 4]!;
      header[5] = bytes[pos + 5]!;
      header[6] = bytes[pos + 6]!;
      header[7] = bytes[pos + 7]!;
      if (headerLen === 16) {
        // Large-size header — keep the extended size bytes too
        for (let i = 8; i < 16; i++) header[i] = bytes[pos + i]!;
      }
      out.push(header);
      out.push(innerBytes);
      pos = boxEnd;
      continue;
    }

    // Top-level `meta` box — also strip (contains iTunes ilst)
    if (topLevel && type === "meta") {
      pos = boxEnd;
      continue;
    }

    // Keep everything else verbatim
    out.push(bytes.slice(pos, boxEnd));
    pos = boxEnd;
  }
}

// ---------------------------------------------------------------------------
// Ogg — strip Vorbis COMMENT packets
// ---------------------------------------------------------------------------

/**
 * Ogg page layout:
 *   "OggS" (4)  version (1)  header_type (1)  granule_pos (8)
 *   serial (4)  seqno (4)  checksum (4)  nseg (1)  lacing (nseg)  data
 *
 * Vorbis identification header is packet type 0x01 on logical stream 0.
 * Comment header is packet type 0x03 followed by "vorbis".
 * Opus has "OpusTags" as the comment header packet.
 *
 * Strategy: rebuild all Ogg pages, replacing any page that starts a
 * Vorbis/Opus comment packet with an empty comment packet (we can't just
 * delete the page because granule positions and sequence numbers must be
 * consistent). The empty replacement has vendor string "" and zero comments.
 */
function _stripOgg(bytes: Uint8Array): Uint8Array {
  const pages: Uint8Array[] = [];
  let pos = 0;

  while (pos + 27 <= bytes.length) {
    // Verify "OggS" capture pattern
    if (
      bytes[pos] !== 0x4f ||
      bytes[pos + 1] !== 0x67 ||
      bytes[pos + 2] !== 0x67 ||
      bytes[pos + 3] !== 0x53
    ) {
      throw new Error("Ogg: invalid capture pattern at " + pos);
    }

    const nseg = bytes[pos + 26]!;
    if (pos + 27 + nseg > bytes.length) throw new Error("Ogg: page header truncated");

    // Compute page data length from lacing values
    let dataLen = 0;
    for (let i = 0; i < nseg; i++) {
      dataLen += bytes[pos + 27 + i]!;
    }

    const headerLen = 27 + nseg;
    const pageEnd = pos + headerLen + dataLen;
    if (pageEnd > bytes.length) throw new Error("Ogg: page data truncated");

    const pageData = bytes.slice(pos + headerLen, pageEnd);

    // Detect Vorbis comment header: starts with 0x03 "vorbis"
    // Detect Opus tags header: starts with "OpusTags"
    const isVorbisComment =
      pageData.length >= 7 &&
      pageData[0] === 0x03 &&
      pageData[1] === 0x76 &&
      pageData[2] === 0x6f &&
      pageData[3] === 0x72 &&
      pageData[4] === 0x62 &&
      pageData[5] === 0x69 &&
      pageData[6] === 0x73;

    const isOpusTags =
      pageData.length >= 8 &&
      pageData[0] === 0x4f &&
      pageData[1] === 0x70 &&
      pageData[2] === 0x75 &&
      pageData[3] === 0x73 &&
      pageData[4] === 0x54 &&
      pageData[5] === 0x61 &&
      pageData[6] === 0x67 &&
      pageData[7] === 0x73;

    if (isVorbisComment || isOpusTags) {
      // Build a minimal replacement comment packet
      const replacement = isVorbisComment
        ? _minimalVorbisComment()
        : _minimalOpusTags();

      // Build a new Ogg page with the replacement data, copying header fields
      // (header_type, granule_pos, serial, seqno) from the original page.
      // We rebuild the lacing table and recompute the CRC.
      const newPage = _buildOggPage(
        bytes,
        pos,
        replacement,
        /* preserveHeaderType */ false,
        /* forceHeaderType */ bytes[pos + 5]! & 0xfe, // clear continued bit
      );
      pages.push(newPage);
    } else {
      pages.push(bytes.slice(pos, pageEnd));
    }

    pos = pageEnd;
  }

  return _concat(pages);
}

/** Minimal Vorbis comment packet: type=0x03, "vorbis", vendor="", 0 comments */
function _minimalVorbisComment(): Uint8Array {
  // 0x03 "vorbis" vendor_length=0 user_comment_list_length=0 framing_bit=1
  return new Uint8Array([
    0x03,
    0x76, 0x6f, 0x72, 0x62, 0x69, 0x73, // "vorbis"
    0x00, 0x00, 0x00, 0x00,              // vendor_length = 0
    0x00, 0x00, 0x00, 0x00,              // user_comment_list_length = 0
    0x01,                                // framing_bit
  ]);
}

/** Minimal OpusTags packet: "OpusTags" + vendor="" + 0 comments */
function _minimalOpusTags(): Uint8Array {
  return new Uint8Array([
    0x4f, 0x70, 0x75, 0x73, 0x54, 0x61, 0x67, 0x73, // "OpusTags"
    0x00, 0x00, 0x00, 0x00,                           // vendor_length = 0
    0x00, 0x00, 0x00, 0x00,                           // user_comment_list_length = 0
  ]);
}

/**
 * Rebuild an Ogg page with `newData`, copying structural fields from the
 * original page header at `src[srcPageOffset]`.
 */
function _buildOggPage(
  src: Uint8Array,
  srcOffset: number,
  newData: Uint8Array,
  _preserveHeaderType: boolean,
  headerTypeByte: number,
): Uint8Array {
  // Build lacing table for newData
  const lacingValues: number[] = [];
  let remaining = newData.length;
  while (remaining >= 255) {
    lacingValues.push(255);
    remaining -= 255;
  }
  lacingValues.push(remaining);

  const nseg = lacingValues.length;
  const headerLen = 27 + nseg;
  const page = new Uint8Array(headerLen + newData.length);

  // Capture pattern
  page[0] = 0x4f; page[1] = 0x67; page[2] = 0x67; page[3] = 0x53;
  page[4] = 0x00; // version
  page[5] = headerTypeByte;
  // granule_pos (8 bytes) from original
  for (let i = 0; i < 8; i++) page[6 + i] = src[srcOffset + 6 + i]!;
  // bitstream serial (4 bytes)
  for (let i = 0; i < 4; i++) page[14 + i] = src[srcOffset + 14 + i]!;
  // page sequence number (4 bytes)
  for (let i = 0; i < 4; i++) page[18 + i] = src[srcOffset + 18 + i]!;
  // checksum placeholder (4 bytes, filled in below)
  page[22] = 0; page[23] = 0; page[24] = 0; page[25] = 0;
  page[26] = nseg;
  for (let i = 0; i < nseg; i++) page[27 + i] = lacingValues[i]!;
  page.set(newData, headerLen);

  // Compute CRC32 over the full page
  const crc = _oggCrc32(page);
  page[22] = (crc >>> 24) & 0xff;
  page[23] = (crc >>> 16) & 0xff;
  page[24] = (crc >>> 8) & 0xff;
  page[25] = crc & 0xff;

  return page;
}

/** Ogg CRC32 — generator polynomial 0x04c11db7, initialised to 0 */
function _oggCrc32(data: Uint8Array): number {
  let crc = 0;
  for (let i = 0; i < data.length; i++) {
    crc ^= (data[i]! << 24) >>> 0;
    for (let j = 0; j < 8; j++) {
      crc = ((crc & 0x80000000) !== 0 ? (crc << 1) ^ 0x04c11db7 : crc << 1) >>> 0;
    }
  }
  return crc >>> 0;
}

// ---------------------------------------------------------------------------
// Internal utility
// ---------------------------------------------------------------------------

function _concat(chunks: Uint8Array[]): Uint8Array {
  const total = chunks.reduce((s, c) => s + c.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.length;
  }
  return out;
}
